import { useEffect, useRef, useState } from 'react';
import type { DuplicateCandidate, Impact, RequestType } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { RequestStatusPill } from '@/components/portal/status-pill';
import { IMPACT_SHORT_LABELS, TYPE_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';
import { Highlight } from './duplicate-panel';

export type PromptChoice = 'same' | 'different';

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-[13px]">
      <span className="w-[72px] shrink-0 text-muted-foreground">{label}</span>
      <span className="text-foreground">{children}</span>
    </div>
  );
}

/**
 * 16 · Vérification à l'envoi (ADR-009). Opens on « Envoyer la demande » when
 * the best candidate reaches the threshold. Never blocking: « C'est
 * différent » sends the request; « Revenir au formulaire » creates nothing.
 */
export function DuplicatePrompt(props: {
  candidate: DuplicateCandidate;
  others: number;
  draft: { title: string; productName: string; type: RequestType; impact: Impact };
  sameProduct: boolean;
  busy: boolean;
  onConfirm: (choice: PromptChoice) => void;
  onBack: () => void;
}) {
  const { candidate: c } = props;
  const [choice, setChoice] = useState<PromptChoice>('same');
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') props.onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  const options: { value: PromptChoice; title: string; text: string }[] = [
    {
      value: 'same',
      title: "C'est le même problème",
      text: `Je m'abonne à ${c.reference}. Ma description et mes pièces jointes y sont ajoutées comme message, et l'équipe produit voit que mon équipe est concernée.`,
    },
    {
      value: 'different',
      title: "C'est différent",
      text: `J'envoie ma demande. L'équipe produit saura qu'elle ressemble à ${c.reference} et que vous les avez jugées différentes.`,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-16">
      <div aria-hidden="true" className="fixed inset-0 bg-overlay" onClick={props.onBack} />
      <div
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="verif-titre"
        className="relative flex w-[760px] max-w-full flex-col overflow-hidden rounded-card bg-card shadow-dialog outline-none"
      >
        <div aria-hidden="true" className="h-1 bg-brand-gradient" />
        <div className="flex flex-col gap-[18px] px-7 py-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-warning-foreground">Avant d'envoyer</span>
            <h2 id="verif-titre" className="m-0 text-[22px] font-black">
              Votre demande ressemble à {c.reference}
            </h2>
            <p className="m-0 text-sm text-foreground-secondary">
              {props.sameProduct ? 'Même produit' : 'Produit à déterminer'}
              {c.commonWords.length > 0 && (
                <>
                  , mots en commun :{' '}
                  {c.commonWords.map((w, i) => (
                    <span key={w}>
                      {i > 0 && ', '}
                      <mark className="rounded-sm bg-warning-border px-0.5 text-inherit">{w}</mark>
                    </span>
                  ))}
                </>
              )}
              . Si c'est le même problème, vous serez prévenu de son avancement sans créer de doublon.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 rounded-control border border-border bg-card px-4 py-3.5">
              <span className="text-xs font-bold uppercase tracking-[0.04em] text-muted-foreground">Votre demande</span>
              <strong className="text-[15px]">
                <Highlight text={props.draft.title} words={c.commonWords} />
              </strong>
              <Line label="Produit">{props.draft.productName}</Line>
              <Line label="Type">{TYPE_LABELS[props.draft.type]}</Line>
              <Line label="Impact">{IMPACT_SHORT_LABELS[props.draft.impact]}</Line>
            </div>
            <div className="flex flex-col gap-1.5 rounded-control border border-warning-border bg-warning-subtle px-4 py-3.5">
              <span className="text-xs font-bold uppercase tracking-[0.04em] text-warning-foreground">{c.reference} · existante</span>
              <strong className="text-[15px]">
                <Highlight text={c.title} words={c.titleMatches} />
              </strong>
              {c.productName && <Line label="Produit">{c.productName}</Line>}
              {!c.readable && <Line label="Équipe">Autre équipe</Line>}
              <Line label="Statut">
                <RequestStatusPill status={c.status} />
              </Line>
              <Line label="Suivie par">
                {c.teamsConcerned > 1 ? `${c.teamsConcerned} équipes` : '1 équipe'}, {c.followers > 1 ? `${c.followers} personnes` : '1 personne'}
              </Line>
            </div>
          </div>

          <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
            <legend className="mb-2 p-0 text-sm font-bold">Que voulez-vous faire ?</legend>
            {options.map((o) => (
              <label
                key={o.value}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-control px-4 py-3.5',
                  choice === o.value ? 'border-2 border-primary bg-accent' : 'border border-border bg-card',
                )}
              >
                <input type="radio" name="choix" className="m-0 mt-[3px] accent-primary" checked={choice === o.value} onChange={() => setChoice(o.value)} />
                <span className="flex flex-col gap-1">
                  <strong className="text-sm">{o.title}</strong>
                  <span className="text-[13px] text-foreground-secondary">{o.text}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {props.others > 0 && (
            <p className="m-0 text-[13px] text-muted-foreground">
              {props.others === 1 ? '1 autre demande proche' : `${props.others} autres demandes proches`} ·{' '}
              <button type="button" className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] font-bold text-primary" onClick={props.onBack}>
                les voir dans le formulaire
              </button>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-7 py-4">
          <Button type="button" variant="secondary" className="min-h-11" disabled={props.busy} onClick={props.onBack}>
            Revenir au formulaire
          </Button>
          <Button type="button" className="min-h-11" disabled={props.busy} onClick={() => props.onConfirm(choice)}>
            {choice === 'same' ? `M'abonner à ${c.reference}` : 'Envoyer ma demande'}
          </Button>
        </div>
      </div>
    </div>
  );
}
